import clsx from 'clsx';
import { ArrowLeft, Check, ChevronDown, FileText, MessageSquare, MessagesSquare, Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useApproveTask, useTask, useTasks, useThreads } from '@/api';
import type { Task } from '@/api/types';
import { AttachmentChip } from '@/components/AttachmentChip';
import { NewDiscussionDialog } from '@/components/NewDiscussionDialog';
import { Thread } from '@/components/Thread';
import { Markdown } from '@/components/Markdown';
import { PersonLink } from '@/components/PersonDialog';
import { failureKind } from '@/lib/errors';
import { QueryPending } from '@/components/QueryState';
import { Avatar, Dialog, Empty, Status, TaskGlyph } from '@/components/ui';
import { formatDate, formatDue, formatWhen } from '@/lib/format';
import { statusOrder, taskLabel, taskStatus } from '@/lib/status';
import { UploadButton } from '@/pages/Documents';
import type { ProjectContext } from './ProjectShell';

type Filter = 'open' | 'client' | 'done' | 'all';
type GroupBy = 'group' | 'status';

const filters: { value: Filter; label: string; test: (t: Task) => boolean }[] = [
  { value: 'open', label: 'Открытые', test: (t) => t.status !== 'done' },
  { value: 'client', label: 'Ждут вас', test: (t) => !!t.needsClient && t.status !== 'done' },
  { value: 'done', label: 'Готово', test: (t) => t.status === 'done' },
  { value: 'all', label: 'Все', test: () => true },
];

/** A task waiting for the client is grouped as «Ждёт вас», whatever its Linear state (backlog included). */
const statusGroup = (t: Task): string => (t.needsClient && t.status !== 'done' ? 'waiting' : t.status);

function Row({ t, selected }: { t: Task; selected: boolean }) {
  const s = taskLabel(t);
  return (
    <li>
      <Link
        to={`/p/${t.projectId}/tasks/${t.id}`}
        aria-current={selected ? 'true' : undefined}
        className={clsx('flex h-10 items-center gap-3 px-4 text-sm transition-colors', selected ? 'bg-sunken' : 'hover:bg-panel')}
      >
        <TaskGlyph status={t.status} waiting={!!t.needsClient && t.status !== 'done'} />
        <span className="num w-14 shrink-0 font-mono text-xs text-faint">{t.key}</span>
        <span className={clsx('min-w-0 flex-1 truncate', t.status === 'done' && 'text-muted')}>{t.title}</span>
        {t.needsClient && t.status !== 'done' && <span className="hidden shrink-0 text-xs text-waiting xl:inline">{s.label.toLowerCase()}</span>}
        {t.comments > 0 && (
          <span className={clsx('inline-flex shrink-0 items-center gap-1 text-xs', t.unread ? 'font-semibold text-ink' : 'text-faint')} title={t.unread ? `${t.unread} новых` : `${t.comments} сообщений`}>
            <MessageSquare className="size-3.5" />
            {t.comments}
          </span>
        )}
        {t.due && t.status !== 'done' && <span className="hidden w-20 shrink-0 text-right text-xs text-muted sm:inline">{formatDue(t.due)}</span>}
        <Avatar person={t.assignee} size="sm" />
      </Link>
    </li>
  );
}

function TaskPanel({ taskId, onBack }: { taskId: string; onBack: () => void }) {
  const query = useTask(taskId);
  const { data: t, error } = query;
  const approve = useApproveTask();
  const [confirming, setConfirming] = useState(false);
  const [talking, setTalking] = useState(false);
  const navigate = useNavigate();
  const { data: threads } = useThreads(t?.projectId, !!t);
  if (error && failureKind(error) === 'notFound') return <Empty title="Задача не найдена" />;
  if (!t) return <QueryPending query={query} />;
  // Discussions opened on purpose about this task (the task's own thread is the one below).
  const linked = threads?.filter((x) => x.taskId === t.id && x.id !== t.threadId) ?? [];
  const s = taskLabel(t);
  const nc = t.needsClient && t.status !== 'done' ? t.needsClient : undefined;

  return (
    <article className="flex h-full min-h-0 flex-col">
      {/* The top part scrolls on its own: a long description must not push the conversation out of the panel. */}
      <header className="max-h-[55%] shrink-0 space-y-4 overflow-y-auto border-b border-line px-5 py-5">
        <div>
          <button onClick={onBack} className="mb-3 inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink lg:hidden">
            <ArrowLeft className="size-4" /> К списку
          </button>
          <div className="font-mono text-xs text-faint">{t.key}</div>
          <h2 className="mt-1 text-lg font-semibold leading-snug tracking-tight">{t.title}</h2>
        </div>
        <dl className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm">
          <div className="flex items-center gap-1.5">
            <dt className="sr-only">Статус</dt>
            <dd>
              <Status tone={s.tone}>{s.label}</Status>
            </dd>
          </div>
          <div className="flex items-center gap-1.5 text-muted">
            <dt className="sr-only">Исполнитель</dt>
            <dd className="inline-flex items-center gap-1.5">
              <PersonLink person={t.assignee} className="inline-flex items-center gap-1.5 rounded hover:underline hover:underline-offset-2">
                <Avatar person={t.assignee} size="sm" />
                {t.assignee.name}
              </PersonLink>
            </dd>
          </div>
          {t.due && (
            <div className="text-muted">
              <dt className="inline">Срок </dt>
              <dd className="inline text-ink">{formatDate(t.due)}</dd>
            </div>
          )}
          <div className="text-muted">
            <dt className="inline">Этап </dt>
            <dd className="inline text-ink">{t.group}</dd>
          </div>
        </dl>

        {nc && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded bg-waiting/[0.08] px-4 py-3 shadow-[inset_0_0_0_1px_rgb(var(--waiting)/0.25)]">
            <div className="text-sm">
              <div className="font-medium">{nc.note}</div>
              <div className="text-muted">{nc.kind === 'approve' ? 'Замечания напишите в обсуждении ниже.' : `${t.assignee.name} ждёт вас${t.due ? `, срок ${formatDue(t.due)}` : ''}.`}</div>
            </div>
            {nc.kind === 'approve' && (
              <button className="btn-primary" onClick={() => setConfirming(true)} disabled={approve.isPending}>
                <Check className="size-4" /> Утвердить
              </button>
            )}
            {nc.kind === 'access' && (
              <Link className="btn-primary" to={`/access?to=${t.assignee.id}&project=${t.projectId}&title=${encodeURIComponent(t.title)}`}>
                Передать доступ
              </Link>
            )}
            {nc.kind === 'materials' && <UploadButton projectId={t.projectId} taskId={t.id} label="Загрузить файлы" />}
          </div>
        )}

        {t.description && <Markdown text={t.description} className="text-sm leading-relaxed" />}
        {t.attachments.length > 0 && (
          <ul className="flex flex-wrap gap-2">
            {t.attachments.map((a) => (
              <li key={a.id || a.name}>
                <AttachmentChip a={a} icon={<FileText className="size-3.5 text-muted" />} className="inline-flex items-center gap-2 rounded bg-panel px-2.5 py-1.5 text-sm shadow-hair" />
              </li>
            ))}
          </ul>
        )}
        <section aria-label="Обсуждения по задаче" className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-xs font-medium text-muted">Отдельные обсуждения{linked.length > 0 ? `, ${linked.length}` : ''}</h3>
            <button className="btn-ghost h-7 px-2 text-xs" onClick={() => setTalking(true)}>
              <Plus className="size-3.5" /> Новое
            </button>
          </div>
          {linked.length > 0 && (
            <ul className="divide-y divide-line overflow-hidden rounded shadow-hair">
              {linked.map((x) => (
                <li key={x.id}>
                  <Link to={`/p/${t.projectId}/talk/${x.id}`} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-panel">
                    <MessagesSquare className="size-3.5 shrink-0 text-faint" />
                    <span className={clsx('min-w-0 flex-1 truncate', x.unread ? 'font-semibold' : 'font-medium')}>{x.title}</span>
                    {x.unread > 0 ? <span className="num shrink-0 rounded-full bg-ink px-1.5 text-[10px] font-semibold leading-4 text-inverse">{x.unread}</span> : <span className="num shrink-0 text-xs text-faint">{x.total}</span>}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
        <p className="text-xs text-faint">
          Создана {formatWhen(t.createdAt)}, обновлена {formatWhen(t.updatedAt)}
        </p>
      </header>
      <NewDiscussionDialog open={talking} onClose={() => setTalking(false)} projectId={t.projectId} tasks={[t]} taskId={t.id} onCreated={(x) => navigate(`/p/${t.projectId}/talk/${x.id}`)} />
      <Dialog open={confirming} onClose={() => setConfirming(false)} title="Утвердить задачу?">
        <div className="space-y-4 p-5">
          <p className="text-sm">
            <span className="font-medium">{t.key}, {t.title}</span>
            <span className="mt-1 block text-muted">Мы закроем задачу как принятую. Если есть замечания, сначала напишите их в обсуждении.</span>
          </p>
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={() => setConfirming(false)}>
              Отмена
            </button>
            <button
              className="btn-primary"
              disabled={approve.isPending}
              onClick={() => approve.mutate(t.id, { onSettled: () => setConfirming(false) })}
            >
              <Check className="size-4" /> Утвердить
            </button>
          </div>
        </div>
      </Dialog>
      <Thread key={t.threadId} threadId={t.threadId} projectId={t.projectId} unread={t.unread} placeholder={`Написать по ${t.key}`} className="flex-1" />
    </article>
  );
}

export function ProjectTasks() {
  const { project } = useOutletContext<ProjectContext>();
  const { taskId } = useParams();
  const navigate = useNavigate();
  const tasksQuery = useTasks(project.id);
  const tasks = tasksQuery.data;
  const [filter, setFilter] = useState<Filter>('open');
  const [groupBy, setGroupBy] = useState<GroupBy>('group');
  const [q, setQ] = useState('');
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const groups = useMemo(() => {
    if (!tasks) return [];
    const test = filters.find((f) => f.value === filter)!.test;
    const needle = q.trim().toLowerCase();
    const list = tasks.filter((t) => test(t) && (!needle || `${t.key} ${t.title}`.toLowerCase().includes(needle)));
    const order =
      groupBy === 'status'
        ? [{ key: 'waiting', title: 'Ждёт вас' }, ...statusOrder.map((s) => ({ key: s, title: taskStatus[s].label }))]
        : project.kind === 'fixed'
          ? project.milestones.map((m) => ({ key: m.title, title: m.title }))
          : [...new Set(tasks.map((t) => t.group))].map((g) => ({ key: g, title: g }));
    return order
      .map((g) => ({
        ...g,
        items: list
          .filter((t) => (groupBy === 'status' ? statusGroup(t) === g.key : t.group === g.key))
          .sort((a, b) => Number(!!b.needsClient) - Number(!!a.needsClient) || statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status) || (a.due ?? '9').localeCompare(b.due ?? '9')),
      }))
      .filter((g) => g.items.length > 0);
  }, [tasks, filter, groupBy, q, project]);

  const shown = groups.reduce((n, g) => n + g.items.length, 0);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
      <div className={clsx('min-w-0', taskId && 'hidden lg:block')}>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <label className="relative min-w-48 flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-faint" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Найти в задачах проекта" className="field h-8 py-0 pl-8 text-sm" />
          </label>
          <div className="flex rounded bg-sunken p-0.5 text-sm" role="radiogroup" aria-label="Группировать">
            {(['group', 'status'] as const).map((g) => (
              <button
                key={g}
                role="radio"
                aria-checked={groupBy === g}
                onClick={() => setGroupBy(g)}
                className={groupBy === g ? 'rounded-sm bg-canvas px-2.5 py-1.5 sm:py-1 font-medium shadow-hair' : 'px-2.5 py-1.5 sm:py-1 text-muted hover:text-ink'}
              >
                {g === 'status' ? 'По статусу' : project.kind === 'fixed' ? 'По этапам' : 'По направлениям'}
              </button>
            ))}
          </div>
        </div>
        <div className="mb-4 flex flex-wrap gap-1.5" role="group" aria-label="Фильтр">
          {filters.map((f) => (
            <button key={f.value} className="chip" aria-pressed={filter === f.value} onClick={() => setFilter(f.value)}>
              {f.label}
              <span className="num opacity-60">{tasks?.filter(f.test).length ?? ''}</span>
            </button>
          ))}
        </div>

        {!tasks ? (
          <QueryPending query={tasksQuery} />
        ) : shown === 0 ? (
          <Empty title={q ? `Нет задач по «${q}»` : 'Здесь пусто'}>{filter === 'client' ? 'От вас сейчас ничего не ждём.' : 'Попробуйте другой фильтр.'}</Empty>
        ) : (
          <div className="overflow-hidden rounded-lg shadow-hair">
            {groups.map((g) => {
              const isCollapsed = collapsed[g.key] ?? (groupBy === 'status' ? g.key === 'done' : false);
              return (
                <section key={g.key} className="border-b border-line last:border-b-0">
                  <button
                    onClick={() => setCollapsed((c) => ({ ...c, [g.key]: !isCollapsed }))}
                    aria-expanded={!isCollapsed}
                    className="flex w-full items-center gap-2 bg-panel px-4 py-2 text-left text-xs font-medium text-muted hover:text-ink"
                  >
                    <ChevronDown className={clsx('size-3.5 transition-transform', isCollapsed && '-rotate-90')} />
                    <span className="text-ink">{g.title}</span>
                    <span className="num">{g.items.length}</span>
                  </button>
                  {!isCollapsed && (
                    <ul className="divide-y divide-line border-t border-line">
                      {g.items.map((t) => (
                        <Row key={t.id} t={t} selected={t.id === taskId} />
                      ))}
                    </ul>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>

      <div className={clsx('min-w-0', !taskId && 'hidden lg:block')}>
        <div className="panel sticky top-[72px] flex h-[calc(100vh-96px)] min-h-[520px] flex-col overflow-hidden">
          {taskId ? (
            <TaskPanel taskId={taskId} onBack={() => navigate(`/p/${project.id}/tasks`)} />
          ) : (
            <div className="grid flex-1 place-items-center p-10 text-center">
              <div>
                <p className="font-medium">Выберите задачу</p>
                <p className="mt-1 text-sm text-muted">Здесь откроются описание, файлы и обсуждение. Задачи, где ждут вас, всегда наверху.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
